import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useRef, useState } from "react";
import { getColumnWidth, setColumnWidth } from "../../../helpers/cookieUtils";
import {
  TableCellLayout,
  Popover,
  PopoverTrigger,
  PopoverSurface,
} from "@fluentui/react-components";
import {
  EditIcon,
  MeatBallsMenuIcon,
  PendingDeleteIcon,
  RemindIcon,
} from "../../../modules/default-svg";
import userService from "../../../store/services/user.service";
import { generalActions } from "../../../store/actions";
import Spin from "../spinner/Spin";
import {
  getAccountCurrency,
  displayDecimalFormatted,
} from "../../../utils/systemSettings";
import {
  compareArrays,
  getEditPermission,
  sortColumnsByOrderlist,
} from "../../../helpers/utils";
import ListingHeader from "../listingHeader/ListingHeader";
import { getCategoryName } from "../../../views/cashFlow/utils/categoryUtils";
import { useColumnConfig } from "./ColumnConfigProvider";
import useNavigation from "../../../utils/useNavigation";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { createSkeletonRows, useTableContextChange } from "./utils/tableUtils";
const BudgetTable = (props) => {
  const {
    isCategoryParent = false,
    rowData,
    setRowData,
    setIsCreate,
    searchHandler,
    tableName,
    dispatch,
    setPageNum,
    pagination,
    handleReminder,
    handleEdit,
    parentType,
    portfolioId,
    programId,
    projectId,
    budgetLoading,
    triggerSearch
  } = props;
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const [filteredRows, setfilteredRows] = useState(null);
  const setting = JSON.parse(localStorage.getItem("settings"));
  const { columnConfig, updateColumnConfig } = useColumnConfig();
  const tableContainerRef = useRef(null);
  const lastScrollTop = useRef(0);
  const [editingHeader, setEditingHeader] = useState(null);
  const [headerValue, setHeaderValue] = useState("");
  const [columnSizing, setColumnSizing] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [popoverStates, setPopoverStates] = useState({});
  const { navigateTo } = useNavigation();
  const history = useHistory();
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const { hasContextChanged } = useTableContextChange({
    contextValues: {
      portfolioId,
      programId,
      projectId,
      parentType
    },
    setIsFetchingMore,
    setRows,
    setRowData,
  }
  );
  const handlePopoverChange = (itemId, isOpen) => {
    setPopoverStates(isOpen ? { [itemId]: isOpen } : {});
  };
  const [colsList, setColumnsList] = useState([]);

  useEffect(() => {
    if (columnConfig?.length) {
      setColumnsList(
        columnConfig.map((col) => ({
          accessorKey: col.accessorKey,
          header: col.customName || col.label,
          customName: col.customName,
          size: getColumnWidth(tableName, col.accessorKey),
          isVisible: col.isVisible,
          isSortable: col.isSortable,
          columnType: col.columnType,
          columnId: col.columnId,
          order: col.order,
          label: col.label,
        }))
      );
    }

  }, [columnConfig]);
  const handleHeaderDoubleClick = (columnId, currentHeader) => {
    setEditingHeader(columnId);
    setHeaderValue(currentHeader);
  };

  const handleHeaderSave = (columnId) => {
    if (!headerValue.trim()) {
      dispatch(
        generalActions.showToast({
          title: "Header name cannot be empty",
          type: "error",
        })
      );
      return;
    }

    const updatedColumnsDB = cols.map((col) => ({
      name: col.Label,
      customName: col.accessorKey === columnId ? headerValue : col.CustomName,
      order: col.Order,
      isVisible: col.Visibility,
    }));

    const payload = {
      Name: tableName,
      Columns: updatedColumnsDB,
    };

    userService
      .putUserLayoutRequest(payload)
      .then(() => { })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Error updating column header",
            type: "error",
          })
        );
      })
      .finally(() => {
        setColumnsList((prevCols) => {
          const newCols = prevCols.map((col) =>
            col.accessorKey === columnId
              ? { ...col, header: headerValue, customName: headerValue }
              : col
          );
          return newCols;
        });
      });

    setEditingHeader(null);
    setHeaderValue("");
  };

  const fetchMoreData = useCallback(() => {
    if (!pagination?.HasNext || budgetLoading || isFetchingMore || hasContextChanged) return;
    setIsFetchingMore(true);
    setPageNum(pagination.CurrentPage + 1);
  }, [pagination, budgetLoading, isFetchingMore, setPageNum, hasContextChanged]);

  useEffect(() => {
    if (!budgetLoading && isFetchingMore) {
      setIsFetchingMore(false);
    }
  }, [budgetLoading, rowData.length]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement) => {
      if (!containerRefElement || budgetLoading || !pagination?.HasNext)
        return;

      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      const scrolledToBottom =
        Math.abs(scrollHeight - scrollTop - clientHeight) < 50;
      const isScrollingDown = scrollTop > lastScrollTop.current;

      lastScrollTop.current = scrollTop;

      if (isScrollingDown && scrolledToBottom) {
        fetchMoreData();
      }
    },
    [fetchMoreData, budgetLoading, pagination]
  );

  const throttledFetchMore = useCallback(
    (() => {
      let timeoutId;
      return (containerRefElement) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          fetchMoreOnBottomReached(containerRefElement);
        }, 100);
      };
    })(),
    [fetchMoreOnBottomReached]
  );

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const handleScroll = () => throttledFetchMore(container);
      container.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, [throttledFetchMore]);

  // Reset scroll position when search is triggered
  useEffect(() => {
    if (tableContainerRef.current && triggerSearch !== undefined) {
      tableContainerRef.current.scrollTop = 0;
      lastScrollTop.current = 0;
    }
  }, [triggerSearch]);

  const getSortingFunction = (col) => {
    switch (col.label) {
      case "Project":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.project?.name || "").toLowerCase();
          const b = (rowB.original.project?.name || "").toLowerCase();
          return a.localeCompare(b);
        };

      case "Client":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.client?.name || "").toLowerCase();
          const b = (rowB.original.client?.name || "").toLowerCase();
          return a.localeCompare(b);
        };
      case "Category":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.category || 0;
          const b = rowB.original.category || 0;
          return a - b;
        };
      case "Payment Code":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.code || 0;
          const b = rowB.original.code || 0;
          return a - b;
        };

      case "Initial Budget":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.initialBudgetCost) || 0;
          const b = parseFloat(rowB.original.initialBudgetCost) || 0;
          return a - b;
        };

      case "Change Request":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.changeRequestsCost) || 0;
          const b = parseFloat(rowB.original.changeRequestsCost) || 0;
          return a - b;
        };

      case "Revised Budget":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.revisedBudgetCost) || 0;
          const b = parseFloat(rowB.original.revisedBudgetCost) || 0;
          return a - b;
        };
      case "Forecast Allocation":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(isCategoryParent ? rowA.original.forecastAllocation : rowA.original.cashflowLine?.totalForecastAllocation) || 0;
          const b = parseFloat(isCategoryParent ? rowB.original.forecastAllocation : rowB.original.cashflowLine?.totalForecastAllocation) || 0;
          return a - b;
        };
      case "Forecast Hours":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(isCategoryParent ? rowA.original.forecastHours : rowA.original.cashflowLine?.totalForecastHours) || 0;
          const b = parseFloat(isCategoryParent ? rowB.original.forecastHours : rowB.original.cashflowLine?.totalForecastHours) || 0;
          return a - b;
        };
      case "Actual Hours":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(isCategoryParent ? rowA.original.actualHours : rowA.original.cashflowLine?.totalActualHours) || 0;
          const b = parseFloat(isCategoryParent ? rowB.original.actualHours : rowB.original.cashflowLine?.totalActualHours) || 0;
          return a - b;
        };
      case "Forecast hourly Cost":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(isCategoryParent ? rowA.original.forecastHourlyCostRate : rowA.original.cashflowLine?.totalForecastHourlyCostRate) || 0;
          const b = parseFloat(isCategoryParent ? rowB.original.forecastHourlyCostRate : rowB.original.cashflowLine?.totalForecastHourlyCostRate) || 0;
          return a - b;
        };
      case "Forecast hourly Bill":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(isCategoryParent ? rowA.original.forecastHourlyBillRate : rowA.original.cashflowLine?.totalForecastHourlyBillRate) || 0;
          const b = parseFloat(isCategoryParent ? rowB.original.forecastHourlyBillRate : rowB.original.cashflowLine?.totalForecastHourlyBillRate) || 0;
          return a - b;
        };
      case "Forecast Cost":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(isCategoryParent ? rowA.original.forecastCost : rowA.original.cashflowLine?.totalForecastCost) || 0;
          const b = parseFloat(isCategoryParent ? rowB.original.forecastCost : rowB.original.cashflowLine?.totalForecastCost) || 0;
          return a - b;
        };
      case "Forecast Bill":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(isCategoryParent ? rowA.original.forecastBill : rowA.original.cashflowLine?.totalForecastBill) || 0;
          const b = parseFloat(isCategoryParent ? rowB.original.forecastBill : rowB.original.cashflowLine?.totalForecastBill) || 0;
          return a - b;
        };
      case "Forecast CV":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(isCategoryParent ? rowA.original.forecastCv : rowA.original.cashflowLine?.totalForecastCv) || 0;
          const b = parseFloat(isCategoryParent ? rowB.original.forecastCv : rowB.original.cashflowLine?.totalForecastCv) || 0;
          return a - b;
        };
      case "Forecast HV":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(isCategoryParent ? rowA.original.forecastHv : rowA.original.cashflowLine?.totalForecastHv) || 0;
          const b = parseFloat(isCategoryParent ? rowB.original.forecastHv : rowB.original.cashflowLine?.totalForecastHv) || 0;
          return a - b;
        };
      case "Invoiced":
        return (rowA, rowB, columnId) => {
          const a =
            parseFloat(
              isCategoryParent
                ? rowA.original.invoiced
                : rowA.original.cashflowLine?.totalInv
            ) || 0;
          const b =
            parseFloat(
              isCategoryParent
                ? rowB.original.invoiced
                : rowB.original.cashflowLine?.totalInv
            ) || 0;
          return a - b;
        };

      case "Spent":
        return (rowA, rowB, columnId) => {
          const a =
            parseFloat(
              isCategoryParent
                ? rowA.original.spent
                : rowA.original.cashflowLine?.totalAc
            ) || 0;
          const b =
            parseFloat(
              isCategoryParent
                ? rowB.original.spent
                : rowB.original.cashflowLine?.totalAc
            ) || 0;
          return a - b;
        };
      case "PV":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.pv) || 0;
          const b = parseFloat(rowB.original.pv) || 0;
          return a - b;
        };

      case "EV":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.ev) || 0;
          const b = parseFloat(rowB.original.ev) || 0;
          return a - b;
        };

      case "ETC":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.etc) || 0;
          const b = parseFloat(rowB.original.etc) || 0;
          return a - b;
        };

      case "BAC":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.bac) || 0;
          const b = parseFloat(rowB.original.bac) || 0;
          return a - b;
        };

      case "CV":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.cv) || 0;
          const b = parseFloat(rowB.original.cv) || 0;
          return a - b;
        };

      case "SV":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.sv) || 0;
          const b = parseFloat(rowB.original.sv) || 0;
          return a - b;
        };

      default:
        return "alphanumeric";
    }
  };

  const getSortDescFirst = (col) => {
    const descFirstColumns = [
      "Invoiced",
      "Spent",
      "PV",
      "EV",
      "ETC",
      "BAC",
      "CV",
      "SV",
    ];
    return descFirstColumns.includes(col.label);
  };

  const handleClientRedirect = (item) => {
    if (parentType !== "Client") {
      // Use navigation hook for direct navigation to client
      navigateTo("Clients", item.client.id, item.client.name);
    } else {
      // Already in client context, just navigate
      history.push(`/clients/${item.client.id}/overview`);
    }
  };

  const handleProjectRedirect = (item) => {
    if (item.project) {
      navigateTo("Projects", item.project.id, item.project.name);
    }
  };

  useEffect(() => {
    if (!colsList?.length && !rowData.length) return;

    const baseRows = [...rowData];

    if (isFetchingMore && pagination?.HasNext) {
      const skeletonRows = createSkeletonRows();
      setRows([...baseRows, ...skeletonRows]);
    } else {
      setRows(baseRows);
    }
  }, [rowData, isFetchingMore, pagination?.HasNext, colsList]);

  useEffect(() => {
    if (!colsList?.length && !rowData.length) return;
    // Remove category column if not a parent category
    let filteredColumns = !isCategoryParent
      ? colsList
      : colsList.filter((col) => col.label !== "Category");

    // Remove Client and Project columns if parentType is not null
    if (parentType !== null) {
      filteredColumns = filteredColumns.filter((col) => {
        return col.label !== "Client";
      });

      if (parentType === "Project")
        filteredColumns = filteredColumns.filter((col) => {
          return col.label !== "Project";
        });
    }

    const columnsOrder = filteredColumns
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item) => item.accessorKey);
    setColumnOrder(columnsOrder);

    const columns = filteredColumns
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((col, index) => {
        return {
          Label: col.label,
          CustomName: col.customName,
          Order: col.order,
          Visibility: col.isVisible,
          accessorKey: col.accessorKey,
          header: col.header,
          enableHiding: true,
          enableColumnActions: false,
          grow: true,
          enableResizing: true,
          size: col.size,
          editVariant: col.columnType,
          enableGlobalFilter: true,
          enableSorting: true,
          sortingFn: getSortingFunction(col),
          sortDescFirst: getSortDescFirst(col),
          Cell: ({ cell, row }) => {
            const item = row.original;
            if (item.isSkeleton) {
              return (
                <div
                  style={{
                    height: "20px",
                    background:
                      "linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite",
                    borderRadius: "8px",
                    width: "100%",
                  }}
                />
              );
            }
            switch (col.label) {
              case "Category":
                return (
                  <div className="d-flex justify-content-between align-items-center inputWidth">
                    <div className="d-flex align-items-center inputWidth">
                      <span
                        className="d-flex w-full text-truncate cursor-pointer"
                        onClick={(e) => {
                          if (getEditPermission("Budget")) {
                            e.stopPropagation();
                            e.preventDefault();
                            handleEdit(item);
                            handlePopoverChange(
                              item.id,
                              !popoverStates[item.id]
                            );
                          }
                        }}
                      >

                        {item.isPendingDelete && (
                          <div className="mr-1">
                            <PendingDeleteIcon />
                          </div>
                        )}
                        {getCategoryName(item.category)}
                      </span>
                    </div>
                    <div className="meatballs-trigger">
                      <Popover
                        open={popoverStates[item.id]}
                        onOpenChange={(e, data) => handlePopoverChange(item.id, data.open || false)}
                        positioning="below-start"
                      >
                        <PopoverTrigger disableButtonEnhancement>
                          <TableCellLayout
                            media={<MeatBallsMenuIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handlePopoverChange(
                                item.id,
                                !popoverStates[item.id]
                              );
                            }}
                          />
                        </PopoverTrigger>
                        <PopoverSurface
                          tabIndex={-1}
                          className="listingPopoverWrapper"
                        >
                          <div className="listingPopoverContent">
                            {getEditPermission("Budget") && (
                              <div
                                className="popItem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleEdit(item);
                                  handlePopoverChange(
                                    item.id,
                                    !popoverStates[item.id]
                                  );
                                }}
                              >
                                <EditIcon />
                                <p className="text-sm-400 ms-12">Edit</p>
                              </div>
                            )}
                            <div
                              className="popItem"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleReminder(item);
                                handlePopoverChange(
                                  item.id,
                                  !popoverStates[item.id]
                                );
                              }}
                            >
                              <RemindIcon />
                              <p className="text-sm-400 ms-12">Remind me</p>
                            </div>
                            {/* {getDeletePermission("Budget") && (
                                <div
                                  className="popItem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handlePopoverChange(
                                      item.id,
                                      !popoverStates[item.id]
                                    );
                                    handleDelete(item);
                                  }}
                                >
                                  <DeleteIcon />
                                  <p className="text-sm-400 ms-12">Delete</p>
                                </div>
                              )} */}
                          </div>
                        </PopoverSurface>
                      </Popover>
                    </div>
                  </div>
                );
              case "Client":
                if (parentType === "Client") return null;
                return item.client ? (
                  <span
                    onClick={() => handleClientRedirect(item)}
                    className="text-truncate cursor-pointer"
                  >
                    {item.client?.name}
                  </span>
                ) : (
                  ""
                );
              case "Project":
                if (parentType === "Project") return null;
                return (
                  <span
                    onClick={() => handleProjectRedirect(item)}
                    className="text-truncate cursor-pointer"
                  >
                    {item.project?.name}
                  </span>
                );
              case "Payment Code":
                return (
                  <div className="d-flex justify-content-between align-items-center inputWidth">
                    <div className="d-flex align-items-center inputWidth">
                      <span
                        className="d-flex w-full text-truncate cursor-pointer"
                        onClick={(e) => {
                          if (getEditPermission("Budget")) {
                            e.stopPropagation();
                            e.preventDefault();
                            handleEdit(item);
                            handlePopoverChange(
                              item.id,
                              !popoverStates[item.id]
                            );
                          }
                        }}
                      >
                        {item.isPendingDelete && (
                          <div className="mr-1">
                            <PendingDeleteIcon />
                          </div>
                        )}
                        {item.code}
                      </span>
                      {isCategoryParent && (
                        <div className="meatballs-trigger">
                          <Popover
                            open={popoverStates[item.id]}
                            onOpenChange={(isOpen) =>
                              handlePopoverChange(item.id, isOpen)
                            }
                            positioning="below-start"
                          >
                            <PopoverTrigger disableButtonEnhancement>
                              <TableCellLayout
                                media={<MeatBallsMenuIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handlePopoverChange(
                                    item.id,
                                    !popoverStates[item.id]
                                  );
                                }}
                              />
                            </PopoverTrigger>
                            <PopoverSurface
                              tabIndex={-1}
                              className="listingPopoverWrapper"
                            >
                              <div className="listingPopoverContent">
                                {getEditPermission("Budget") && (
                                  <div
                                    className="popItem"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleEdit(item);
                                      handlePopoverChange(
                                        item.id,
                                        !popoverStates[item.id]
                                      );
                                    }}
                                  >
                                    <EditIcon />
                                    <p className="text-sm-400 ms-12">Edit</p>
                                  </div>
                                )}
                                <div
                                  className="popItem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleReminder(item);
                                    handlePopoverChange(
                                      item.id,
                                      !popoverStates[item.id]
                                    );
                                  }}
                                >
                                  <RemindIcon />
                                  <p className="text-sm-400 ms-12">Remind me</p>
                                </div>
                              </div>
                            </PopoverSurface>
                          </Popover>
                        </div>
                      )}
                    </div>
                  </div>
                );
              case "Initial Budget":
                return `${getAccountCurrency(
                  setting
                )} ${displayDecimalFormatted(setting, item.initialBudgetCost)}`;
              case "Change Request":
                return `${getAccountCurrency(
                  setting
                )} ${displayDecimalFormatted(
                  setting,
                  item.changeRequestsCost
                )}`;
              case "Revised Budget":
                return `${getAccountCurrency(
                  setting
                )} ${displayDecimalFormatted(setting, item.revisedBudgetCost)}`;
              case "Forecast Allocation":
                return `${displayDecimalFormatted(
                  setting,
                  isCategoryParent
                    ? Number(item.forecastAllocation)
                    : Number(item.cashflowLine?.totalForecastAllocation || 0)
                )}`;
              case "Forecast Hours":
                return `${displayDecimalFormatted(
                  setting,
                  isCategoryParent
                    ? Number(item.forecastHours)
                    : Number(item.cashflowLine?.totalForecastHours || 0)
                )}`;
              case "Actual Hours":
                return `${displayDecimalFormatted(
                  setting,
                  isCategoryParent
                    ? Number(item.actualHours)
                    : Number(item.cashflowLine?.totalActualHours || 0)
                )}`;
              case "Forecast hourly Cost":
                return `${getAccountCurrency(setting)} ${displayDecimalFormatted(
                  setting,
                  isCategoryParent
                    ? Number(item.forecastHourlyCostRate)
                    : Number(item.cashflowLine?.totalForecastHourlyCostRate || 0)
                )}`;
              case "Forecast hourly Bill":
                return `${getAccountCurrency(setting)} ${displayDecimalFormatted(
                  setting,
                  isCategoryParent
                    ? Number(item.forecastHourlyBillRate)
                    : Number(item.cashflowLine?.totalForecastHourlyBillRate || 0)
                )}`;
              case "Forecast Cost":
                return `${getAccountCurrency(setting)} ${displayDecimalFormatted(
                  setting,
                  isCategoryParent
                    ? Number(item.forecastCost)
                    : Number(item.cashflowLine?.totalForecastCost || 0)
                )}`;
              case "Forecast Bill":
                return `${getAccountCurrency(setting)} ${displayDecimalFormatted(
                  setting,
                  isCategoryParent
                    ? Number(item.forecastBill)
                    : Number(item.cashflowLine?.totalForecastBill || 0)
                )}`; 
              case "Forecast CV":
                const totalForecastCv = isCategoryParent ? Number(item.forecastCv) : Number(item.cashflowLine?.totalForecastCv || 0);
                return (
                  <div
                    className={`flex items-center w-100 px-2 h-[28px] ${totalForecastCv <0 ? "RedFieldBg" : "GreenFieldBg"}`}
                  >
                    {getAccountCurrency(setting)}
                    {displayDecimalFormatted(setting, totalForecastCv)}
                  </div>
                );
              case "Forecast HV":
                const totalForecastHv = isCategoryParent ? Number(item.forecastHv) : Number(item.cashflowLine?.totalForecastHv || 0);
                return (
                  <div
                    className={`flex items-center w-100 px-2 h-[28px] ${totalForecastHv <0 ? "RedFieldBg" : "GreenFieldBg"}`}
                  >
                    {displayDecimalFormatted(setting, totalForecastHv)}
                  </div>
                );
              case "Invoiced":
                return `${getAccountCurrency(
                  setting
                )} ${displayDecimalFormatted(
                  setting,
                  isCategoryParent
                    ? Number(item.invoiced)
                    : Number(item.cashflowLine?.totalInv || 0)
                )}`;
              case "Accural":
                return `${getAccountCurrency(
                  setting
                )} ${displayDecimalFormatted(
                  setting,
                  isCategoryParent
                    ? item.accural
                    : item.cashflowLine?.totalAccural
                )}`;
              case "Spent":
                var spent = isCategoryParent
                  ? item.spent
                  : item.cashflowLine?.totalSpent || 0;
                return `${getAccountCurrency(
                  setting
                )} ${displayDecimalFormatted(setting, spent)}`;
              case "Actual":
                var actual = isCategoryParent
                  ? item.actual
                  : item.cashflowLine?.totalAc;
                return `${getAccountCurrency(
                  setting
                )} ${displayDecimalFormatted(setting, actual)}`;
              case "PV":
                return `${getAccountCurrency(
                  setting
                )} ${displayDecimalFormatted(setting, item.pv)}`;
              case "EV":
                return `${getAccountCurrency(
                  setting
                )} ${displayDecimalFormatted(setting, item.ev)}`;
              case "ETC":
                return `${getAccountCurrency(
                  setting
                )} ${displayDecimalFormatted(setting, item.etc)}`;
              case "BAC":
                const isOverBudget =
                  Number(item.revisedBudgetCost) < Number(item.bac);
                return (
                  <div
                    className={`flex items-center w-100 px-2 h-[28px] ${isOverBudget ? "RedFieldBg" : "GreenFieldBg"
                      }`}
                  >
                    {getAccountCurrency(setting)}
                    {displayDecimalFormatted(setting, item.bac)}
                  </div>
                );
              case "CV":
                return (
                  <div
                    className={`flex items-center w-100 px-2 h-[28px] ${Number(item.revisedBudgetCost) < Number(item.bac)
                      ? "RedFieldBg"
                      : "GreenFieldBg"
                      }`}
                  >
                    {getAccountCurrency(setting)}
                    {displayDecimalFormatted(setting, item.cv)}
                  </div>
                );
              case "SV":
                return (
                  <div className="flex items-center w-100 px-2 BlueFieldBg h-[28px]">
                    {getAccountCurrency(setting)}
                    {displayDecimalFormatted(setting, item.sv ?? 0)}
                  </div>
                );
              default:
                return cell.getValue();
            }
          },
          Header: ({ column }) => {
            return editingHeader === column.id ? (
              <input
                value={headerValue}
                onChange={(e) => setHeaderValue(e.target.value)}
                onBlur={() => handleHeaderSave(column.id)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleHeaderSave(column.id);
                  }
                }}
                autoFocus
                className="header-edit-input"
                style={{
                  minWidth: column.getSize() - 60,
                }}
              />
            ) : (
              <span>{column.columnDef.header}</span>
            );
          },
        };
      });
    setCols((prevCols) => {
      if (JSON.stringify(prevCols) !== JSON.stringify(columns)) {
        return columns;
      }
      return prevCols;
    });
    setRows(rowData);
  }, [colsList, rowData, editingHeader, headerValue]);

  const handleReorderColumns = (results) => {
    if (results?.length) {
      const updatedColumnsDB = results.map((col) => ({
        name: col.label,
        customName: col.customName ?? "",
        order: col.order,
        isVisible: col.isVisible,
      }));

      const payload = {
        Name: tableName,
        Columns: updatedColumnsDB,
      };
      userService
        .putUserLayoutRequest(payload)
        .then(() => {
          dispatch(
            generalActions.showToast({
              title: "Column reordered successfully",
              type: "success",
            })
          );
        })
        .catch((err) => {
          dispatch(
            generalActions.showToast({
              title: "Error updating column ordering",
              type: "error",
            })
          );
        })
        .finally(() => {
          updateColumnConfig(results);
        });
    }
  };

  const handlesearch = (searchText) => {
    if (!searchText) {
      setfilteredRows(null);
    }
    else {
      setfilteredRows(rows.filter((row) => {
        return row.code.toLowerCase().includes(searchText.toLowerCase());
      }))
    }

  };

  const table = useMaterialReactTable({
    columns: cols,
    data: filteredRows || rows,
    enableHiding: true,
    enableSorting: true,
    manualSorting: false,
    visibleInShowHideMenu: false,
    enablePagination: false,
    enableTopToolbar: true,
    enableBottomToolbar: isCategoryParent ? false : true,
    enableGlobalFilter: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    editDisplayMode: "cell",
    globalFilterFn: "contains",
    enableEditing: false,
    enableFilters: false,
    enableCreatingRow: true,
    createDisplayMode: "row",
    positionCreatingRow: "bottom",
    initialState: {
      density: "compact",
      showGlobalFilter: false,
      showColumnFilters: false,
      enableHiding: false,
      columnOrder: cols.map((col) => col.order),
    },
    state: {
      sorting,
      isLoading: rows.length === 0 && budgetLoading,
      columnSizing,
      columnOrder,
      showLoadingOverlay: false,
      showProgressBars: false,
    },
    onSortingChange: setSorting,
    positionGlobalFilter: "left",
    onColumnSizingChange: (updater) => {
      setColumnSizing((prev) => {
        const newSizing =
          typeof updater === "function" ? updater(prev) : updater;
        Object.entries(newSizing).forEach(([columnId, width]) => {
          setColumnWidth(tableName, columnId, width);
        });
        return newSizing;
      });
    },
    onColumnOrderChange: (updater) => {
      const newOrder = typeof updater === "function" ? updater() : updater;
      const neworderList = newOrder
        .filter((item) => item !== "mrt-row-spacer")
        .filter(Boolean);
      const areEqual = compareArrays(neworderList, columnOrder);
      if (areEqual) {
        return newOrder;
      }
      const sortedColumns = sortColumnsByOrderlist(colsList, neworderList);
      handleReorderColumns(sortedColumns);
      setColumnOrder(neworderList);
      return neworderList;
    },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        fontFamily: "Figtree-Medium",
        fontSize: "14px",
        padding: "1rem 0.5rem 1rem 1rem",
        backgroundColor: "#f8f7fc",
        color:"#342c82",
        height: "40px",
        fontWeight: 600,
        "&:hover": {
          backgroundColor: "#ece8fd",
          "& .MuiDivider-root": {
            opacity: 1,
          },
          "& .MuiButtonBase-root": {
            opacity: 1,
          },
          fontWeight: 700,
        },
        position: "relative",
        "& .Mui-TableHeadCell-Content": {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          height: "100%",
        },
        "& .MuiTableSortLabel-root": {
          marginLeft: "8px",
          paddingTop: "3px",
          opacity: 0,
          "&:hover": {
            opacity: 1,
          },
        },
        "& .MuiDivider-root": {
          cursor: "col-resize",
          backgroundColor: "#342C82",
          opacity: 0,
        },
        "& .MuiButtonBase-root": {
          cursor: "grab",
          opacity: 0,
          "& .MuiSvgIcon-root": {
            path:{
              fill: "#342C82"
            }
          }
        },
      },
      onDoubleClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        handleHeaderDoubleClick(column.id, column.columnDef.header);
      },
    }),
    muiTopToolbarProps: {
      showColumnFilters: false,
      showGlobalFilter: true,
      enableHiding: false,
      sx: {
        position: "sticky",
        top: 0,
        zIndex: 1,
        "& .MuiBox-root": {
          padding: 0,
          display: "flex",
          alignItems: "center",
        },
      },
    },
    muiTableBodyProps: {
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",

        marginBottom: isCategoryParent ? 0 : "60px",
        "& .MuiTableBody-Cell": {
          "&:hover": {
            backgroundColor: "#ece8fd15",
          },
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        height: "32px",
        position: "relative",
        "&:hover": {
          backgroundColor: row.original.isSkeleton ? "transparent" : "#ece8fd15",
        },
        cursor: row.original.isSkeleton ? "default" : "pointer",
      },
      className: row.original.isSkeleton ? "" : "hoverable-row",
    }),
    muiTableContainerProps: ({ table }) => ({
      ref: tableContainerRef,
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight || parentType
          ? 115
          : 70
          }px)`,
        overflow: "auto",
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#342c82",
          borderRadius: "8px",
          "&:hover": {
            backgroundColor: "#342c82",
          },
        },
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiSkeletonProps: {
      height: 34,
      sx: {
        background:
          "linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
        "@keyframes shimmer": {
          "0%": {
            backgroundPosition: "200% 0",
          },
          "100%": {
            backgroundPosition: "-200% 0",
          },
        },
      },
    },
    muiTableBodyCellProps: ({ cell, column, table }) => ({
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",
        padding: "0.5rem 1rem",
        position: "relative",
        overflow: "visible",
        "&:focus": {
          outline: "none",
        },
        "&:hover": {
          fontFamily: "Figtree-Medium",
          fontWeight: "bold",
        },
      },
    }),
    renderTopToolbar: () =>
    (
      <ListingHeader
        isHeader={true}
        rightPane={true}
        parentType={parentType ?? "Budget"}
        type={isCategoryParent ? "Budget-Category" : "Budget"}
        btnLable="Add Budget"
        setIsCreate={() => setIsCreate(true)}
        portfolioId={portfolioId}
        programId={programId}
        projectId={projectId}
        isCreatable={false}
        isSearchDisable={cols.length}
        searchedData={isCategoryParent ? handlesearch : searchHandler}
        triggerSearch={isCategoryParent ? handlesearch : triggerSearch}
      />
    ),
    renderBottomToolbarCustomActions: () => (
      <div className="bottom-toolbar-container">
        <div className="toolbar-content">
          {budgetLoading || isFetchingMore ? (
            <div className="loading-indicator">
              <span>{!isFetchingMore ? 'Loading...' : 'Loading more data...'}</span>
            </div>
          ) : pagination?.TotalCount > 0 ? (
            <div className="data-info">
              <span className="data-text">
                Showing <strong>{rowData.length}</strong> of{" "}
                <strong>{pagination?.TotalCount || 0}</strong> total records
              </span>
            </div>
          ) : (
            <div className="data-info">
              <span className="data-text">No records Found</span>
            </div>
          )}
        </div>
      </div>
    ),
    muiBottomToolbarProps: {
      sx: {
        position: "absolute",
        bottom: 0,
        backgroundColor: "#f8f7fc",
        padding: "0.7rem 1rem",
        width: "100%",
        height: "38px",
      },
    },
    muiLinearProgressProps: {
      sx: {
        height: "4px",
        backgroundColor: "#cfc9ff",
        "& .MuiLinearProgress-bar": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-bar1Indeterminate": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-bar2Indeterminate": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-barColorPrimary": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-colorPrimary": {
          backgroundColor: "#cfc9ff",
        },
        "& .MuiLinearProgress-colorSecondary": {
          backgroundColor: "#cfc9ff",
        },
        "& .MuiLinearProgress-barColorSecondary": {
          backgroundColor: "#342c82",
        },
      },
    },
    renderEmptyRowsFallback: () => {
      return budgetLoading ? (
        <div className="d-flex justify-content-center">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="h-full flex flex-col items-center justify-center min-h-[400px] p-8">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="65"
              height="64"
              viewBox="0 0 65 64"
              fill="none"
              className="mb-4"
            >
              <path
                d="M6.9002 21.6008C6.9002 16.7407 10.8401 12.8008 15.7002 12.8008H49.3002C54.1603 12.8008 58.1002 16.7407 58.1002 21.6008V42.4008C58.1002 47.2609 54.1603 51.2008 49.3002 51.2008H32.8884L27.8968 46.2092C28.7964 44.3293 29.3002 42.2239 29.3002 40.0008C29.3002 32.0479 22.8531 25.6008 14.9002 25.6008C11.94 25.6008 9.18835 26.494 6.9002 28.0257V21.6008ZM14.9002 51.2008C17.416 51.2008 19.7381 50.3713 21.6079 48.9708L29.7688 57.132C30.3936 57.7569 31.4067 57.7569 32.0315 57.1321C32.6564 56.5072 32.6564 55.4942 32.0316 54.8693L23.8706 46.708C25.2708 44.8384 26.1002 42.5164 26.1002 40.0008C26.1002 33.8152 21.0858 28.8008 14.9002 28.8008C8.71461 28.8008 3.7002 33.8152 3.7002 40.0008C3.7002 46.1864 8.71461 51.2008 14.9002 51.2008ZM14.9002 48.0008C10.4819 48.0008 6.9002 44.4191 6.9002 40.0008C6.9002 35.5825 10.4819 32.0008 14.9002 32.0008C19.3185 32.0008 22.9002 35.5825 22.9002 40.0008C22.9002 44.4191 19.3185 48.0008 14.9002 48.0008Z"
                fill="#342C82"
              />
            </svg>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No data currently available!
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              New entries will appear here once created.
            </p>
          </div>
        </div>
      ) : null;
    },
  });

  return <MaterialReactTable table={table} />;
};

export default BudgetTable;
